/**
 * How much model a device can hold.
 *
 * An iPhone tab is killed well before the 2.8 GB the production Design page reached with its model loaded (30 Oct
 * 2026). So a phone gets a lighter model: a lower pixel ratio, coarser ground, a smaller tile cache, vegetation off
 * until asked for, and conductors and pillows only near the view. Where the browser reports its memory the tier follows
 * it. iOS reports nothing, so a phone that says nothing is treated as light. A desktop that says nothing (Safari,
 * Firefox) keeps the full model, as before. As mighty-map-esri's Mighty BIM core/budget.ts.
 */
import type { RenderBudget } from '@mighty-map/map-engine/bim';

export type Budget = 'light' | 'standard' | 'full';

export interface BudgetSpec extends RenderBudget {
  /** Vegetation shown when the model opens. */
  vegOn: boolean;
  /** Share of the configured vegetation points and radius. */
  vegScale: number;
  /** Conductors and pillows only within this many metres of the view, and at most this many spans. Null keeps the
   *  project's own. */
  near: { radius: number; maxLines: number } | null;
}

export const BUDGETS: Record<Budget, BudgetSpec> = {
  full: { pixelRatio: 2, liveError: 6, cache: null, vegOn: true, vegScale: 1, near: null },
  standard: {
    pixelRatio: 1.5,
    liveError: 8,
    cache: { minSize: 3000, maxSize: 4000, minMB: 200, maxMB: 260 },
    vegOn: true,
    vegScale: 0.5,
    near: { radius: 4000, maxLines: 2500 },
  },
  light: {
    pixelRatio: 1.5,
    liveError: 12,
    cache: { minSize: 1500, maxSize: 2000, minMB: 110, maxMB: 150 },
    vegOn: false,
    vegScale: 0.25,
    near: { radius: 2500, maxLines: 1500 },
  },
};

const ORDER: Budget[] = ['full', 'standard', 'light'];

export interface DeviceInfo {
  /** The primary pointer is coarse: a phone or a tablet. A laptop with a touch screen has a fine one. */
  touchFirst: boolean;
  /** `navigator.deviceMemory` in GB, where the browser reports it. */
  memoryGB?: number;
  /** A `?bimBudget=` override, for testing on a desktop. */
  override?: string | null;
}

export function readDevice(): DeviceInfo {
  let touchFirst = false;
  let override: string | null = null;
  try {
    touchFirst = matchMedia('(pointer: coarse)').matches;
  } catch {
    /* no matchMedia: not a phone */
  }
  try {
    override = new URLSearchParams(location.search).get('bimBudget');
  } catch {
    /* no location */
  }
  // No navigator (Node, the tests): no memory reading.
  const dm = typeof navigator === 'undefined' ? NaN : Number((navigator as { deviceMemory?: number }).deviceMemory);
  return { touchFirst, override, ...(Number.isFinite(dm) && dm > 0 ? { memoryGB: dm } : {}) };
}

export function deviceBudget(d: DeviceInfo = readDevice()): Budget {
  if (d.override && d.override in BUDGETS) return d.override as Budget;
  const dm = d.memoryGB;
  if (d.touchFirst) return dm == null || dm < 4 ? 'light' : 'standard';
  if (dm == null) return 'full';
  return dm < 4 ? 'light' : dm < 8 ? 'standard' : 'full';
}

/** The next tier down, or null at the bottom. */
export function lowerBudget(b: Budget): Budget | null {
  const i = ORDER.indexOf(b);
  return i >= 0 && i < ORDER.length - 1 ? (ORDER[i + 1] ?? null) : null;
}

/** A conductor or pillow limit under the budget: the smaller of the project's own and the budget's. */
export function nearLimit(spec: BudgetSpec, k: 'radius' | 'maxLines', n: number): number {
  return spec.near ? Math.min(n, spec.near[k]) : n;
}
